import React, { useMemo } from "react"
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig } from "remotion"
import { msToFrames } from "../../utils/timing"
import { loadFont } from "../../utils/fonts"
import { estimateTextBoxHeight, placeTextBox, placementYAt, type Placement, type TimedBand } from "../../utils/placement"
import type { SceneMetadata, Track } from "../../schemas"

interface HookTrackProps {
  track: Track
  sceneMetadata?: SceneMetadata
  /** What else is on screen that the text must not cover (the captions). */
  avoid?: TimedBand[]
}

const DEFAULT_POSITION = { xPct: 0, yPct: -0.6, wPct: 0.9, hPct: 0.15 }
const NO_BANDS: TimedBand[] = []

/** A text box still at the default "top" spot is placed clear of faces; one the user moved stays put. */
export function isAutoPlaced(position: { xPct?: number; yPct?: number } | undefined): boolean {
  return !position || ((position.xPct ?? 0) === DEFAULT_POSITION.xPct && position.yPct === DEFAULT_POSITION.yPct)
}

export const HookTrack: React.FC<HookTrackProps> = ({ track, sceneMetadata, avoid = NO_BANDS }) => {
  const { fps, width, height, durationInFrames } = useVideoConfig()

  // Flatten all hook segments from sections
  const segments = track.sections.flatMap(s => s.segments)

  // Auto-placed boxes in start order, each clear of faces, the captions and the boxes placed before it.
  const placements = useMemo(() => {
    const placed = new Map<string, Placement[]>()
    const taken: TimedBand[] = [...avoid]
    for (const segment of track.sections.flatMap(s => s.segments).sort((a, b) => a.timeline.in - b.timeline.in)) {
      if (!segment.content || !isAutoPlaced(segment.position)) continue
      const style = segment.content.textStyle ?? {}
      const fontSize = Math.max(20, Math.min(style.fontSize ?? 26, 44)) * (segment.content.scale ?? 1)
      const text = (segment.content.textElements ?? []).map((el: { text: string }) => el.text).join(" ")
      const boxWidth = Math.min(segment.position?.wPct ?? 0.9, 0.92) * width
      const padding = typeof style.padding === "number" ? style.padding : 12
      const boxHeight = estimateTextBoxHeight(text, fontSize, boxWidth, padding, height)
      const startSec = segment.timeline.in / 1000
      const endSec = segment.timeline.out / 1000
      const spots = placeTextBox({ meta: sceneMetadata, fps, totalFrames: durationInFrames, width, height, startSec, endSec, boxHeight, avoid: taken })
      placed.set(segment.id, spots)
      spots.forEach((spot, i) => taken.push({
        startSec: spot.startSec,
        endSec: spots[i + 1]?.startSec ?? endSec,
        y0: spot.y - boxHeight / 2,
        y1: spot.y + boxHeight / 2,
      }))
    }
    return placed
  }, [track, avoid, sceneMetadata, fps, durationInFrames, width, height])

  return (
    <AbsoluteFill>
      {segments.map((segment) => {
        const inFrame = msToFrames(segment.timeline.in, fps)
        const duration = msToFrames(segment.timeline.out - segment.timeline.in, fps)
        if (!segment.content) return null
        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={Math.max(duration, 1)}>
            <HookBox segment={segment} startFrame={inFrame} placements={placements.get(segment.id)} />
          </Sequence>
        )
      })}
    </AbsoluteFill>
  )
}

const HookBox: React.FC<{ segment: Track["sections"][number]["segments"][number]; startFrame: number; placements?: Placement[] }> = ({ segment, startFrame, placements }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const content = segment.content
  const style = content.textStyle ?? {}
  const position = segment.position ?? DEFAULT_POSITION

  // Load the font
  if (style.fontFamily) loadFont(style.fontFamily)

  // Convert CPct position to CSS; an auto-placed box takes its face-aware centre instead.
  const left = `${50 + (position.xPct ?? 0) * 50}%`
  const top = placements ? `${placementYAt(placements, (startFrame + frame) / fps) * 100}%` : `${50 + (position.yPct ?? 0) * 50}%`
  const width = `${(position.wPct ?? 0.9) * 100}%`
  const fontSize = Math.max(20, Math.min(style.fontSize ?? 26, 44))
  const backgroundColor = style.backgroundColor ?? "rgba(0,0,0,0.78)"
  const fontColor = style.fontColor ?? "#FFFFFF"

  const text = (content.textElements ?? []).map((el: { text: string }) => el.text).join(" ")

  return (
    <AbsoluteFill>
      <div
        style={{
          position: "absolute",
          left,
          top,
          transform: `translate(-50%, -50%) scale(${content.scale ?? 1}) rotate(${content.rotation ?? 0}deg)`,
          width,
          maxWidth: "92%",
          backgroundColor,
          color: fontColor,
          fontFamily: style.fontFamily ?? "Montserrat",
          fontWeight: style.fontWeight ?? "900",
          fontSize,
          fontStyle: style.italic ? "italic" : "normal",
          borderRadius: style.borderRadius ?? 10,
          padding: style.padding ?? "10px 12px",
          textAlign: (style.textAlign as React.CSSProperties["textAlign"]) ?? "center",
          border: style.borderColor ? `${style.borderWidth ?? 0}px solid ${style.borderColor}` : "none",
          boxSizing: "border-box",
          lineHeight: 1.2,
          letterSpacing: "0.01em",
          textShadow: "0 1px 3px rgba(0,0,0,0.45)",
          textRendering: "optimizeLegibility",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  )
}
